import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  HStack,
  Stack,
  Text,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  type BuyerParticipationRequestDetailDto,
  type BuyerBidEspdDto,
  type WithdrawnRejectionDto,
  ParticipationRequestStatus,
  useGetParticipationRequestRejectionDocumentsQuery,
  useGetSourcingEventQuery,
  useSetQualificationVerdictMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { ReasonModal } from "../../../../common/ReasonModal";
import { useExclusiveAction } from "../../../../common/hooks/useExclusiveAction";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { RejectionDetails } from "../../../../common/rejection/RejectionDetails";
import { rejectionVenueOf } from "../../../../common/rejection/rejectionMessage";
import {
  type RejectionSubmission,
  RejectSupplierModal,
} from "../../../../common/rejection/RejectSupplierModal";
import { getQualificationRequirementGroundKey, rejectionRegimeOf } from "../../../../common/rejection/rejectionGrounds";
import { QualificationVerdictControl } from "../../bid-detail/QualificationVerdictControl";
import { DeclaredExclusionGrounds, ExclusionGroundsAssessmentRecord } from "./DeclaredExclusionGrounds";
import { formatDecidedBy } from "./formatDecidedBy";
import { getRequestQualificationProgress } from "./requestQualification";
import { useBuyerReviewNotesEditor } from "./useBuyerReviewNotesEditor";
import { SaveStatusBadge } from "../../../../common/editing/SaveStatusBadge/SaveStatusBadge";
import { participationStatusBadge } from "../../../../common/qualification/participationStatusBadge";
import { displayDate } from "../../../../common/formatting/displayDate";
import { lastDayOnListBefore } from "../../dps/removalNotice";

interface Props {
  eventId: string;
  detail: BuyerParticipationRequestDetailDto;
  optimisticReviewed?: number;
  onShowEspd?: () => void;
  mayManage: boolean;
}

// Counts criteria that have answer !== OK (deviations + unanswered).
const deriveEspdDigest = (espd: BuyerBidEspdDto): { flagged: number; total: number } => {
  const flagged = (espd.deviationCount ?? 0) + (espd.unansweredCount ?? 0);
  const total = (espd.okCount ?? 0) + flagged;
  return { flagged, total };
};

export const QualificationDecisionCard = ({ eventId, detail, optimisticReviewed, onShowEspd, mayManage }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const apiError = useApiError();
  const [setVerdict] = useSetQualificationVerdictMutation();
  const rejectionModal = useDisclosure();
  const withdrawalModal = useDisclosure();
  const [withdrawalReason, setWithdrawalReason] = useState("");
  const [alreadyWithdrawn, setAlreadyWithdrawn] = useState(false);

  const request = detail.request;
  const verdict = request.qualificationVerdict ?? null;
  const locked = detail.invitationConfirmed ?? false;
  const clearable = detail.verdictClearable ?? true;
  const endsByRemoval = !!detail.admissionEndsByRemoval && verdict?.qualified === true;
  const isRejected = verdict?.qualified === false;
  const membershipEnded =
    !!request.membershipEndedAt &&
    (request.status === ParticipationRequestStatus.Removed || request.status === ParticipationRequestStatus.Withdrawn);
  // The BFF always returns an ESPD DTO; hasResponse=false means no ESPD was submitted.
  const espdDigest = detail.espd?.hasResponse ? deriveEspdDigest(detail.espd) : null;

  const editor = useBuyerReviewNotesEditor(eventId, request.id, {
    internalNote: request.internalNote,
    rejectionReason: request.rejectionReason,
    exclusionGroundsAssessment: request.exclusionGroundsAssessment,
  });
  const declaredGrounds = detail.declaredExclusionGrounds ?? [];
  const recordedAssessment = verdict?.qualified ? verdict.exclusionGroundsAssessment : null;
  const assessmentRequired = detail.exclusionGroundsAssessmentRequired ?? false;
  const assessmentMissing = assessmentRequired && editor.values.exclusionGroundsAssessment.trim() === "";

  const progress = getRequestQualificationProgress(
    detail.selectionCriteria ?? [],
    request.qualificationResponses ?? [],
  );

  // The "all reviewed" gate respects an optimistic count so the parent can unblock
  // the Qualified button before the refetch settles.
  const effectiveReviewed = optimisticReviewed ?? progress.reviewed;
  const allReviewed = !progress.hasRequirements || effectiveReviewed >= progress.total;
  const pendingCount = progress.hasRequirements ? progress.total - effectiveReviewed : 0;
  const awaitsQualification = verdict?.qualified !== true;

  const [runVerdictAction, isSettingVerdict] = useExclusiveAction((action: () => Promise<void>) => action());

  const submitVerdict = (qualified: boolean | null) =>
    runVerdictAction(async () => {
      try {
        await editor.flush();
        await setVerdict({
          eventId,
          requestId: request.id,
          setQualificationVerdictRequestDto: { qualified },
        }).unwrap();
      } catch (e) {
        apiError.trigger(e);
      }
    });

  const handleVerdictChange = async (value: boolean | null) => {
    // Disqualifying is never a bare toggle — it goes through the structured § 24-2 / § 9-5 flow.
    if (value === false) {
      rejectionModal.onOpen();
      return;
    }
    if (value === true && isRejected && !clearable) {
      setWithdrawalReason("");
      setAlreadyWithdrawn(false);
      withdrawalModal.onOpen();
      return;
    }
    await submitVerdict(value);
  };

  const submitWithdrawal = () =>
    runVerdictAction(async () => {
      try {
        await editor.flush();
        await setVerdict({
          eventId,
          requestId: request.id,
          setQualificationVerdictRequestDto: { qualified: true, withdrawalReason },
        }).unwrap();
        withdrawalModal.onClose();
      } catch (e) {
        if (errorCodeOf(e) === REJECTION_ALREADY_WITHDRAWN) {
          setAlreadyWithdrawn(true);
        } else {
          apiError.trigger(e);
        }
      }
    });

  const submitRejection = (submission: RejectionSubmission) =>
    runVerdictAction(async () => {
      await editor.flush();
      await setVerdict({
        eventId,
        requestId: request.id,
        setQualificationVerdictRequestDto: {
          qualified: false,
          rejectionReason: submission.reason,
          rejection: {
            grounds: submission.grounds,
            message: submission.message,
            groundTexts: submission.groundTexts,
            supplierName: submission.supplierName,
            attachmentIds: submission.attachmentIds,
          },
        },
      }).unwrap();
      rejectionModal.onClose();
    });

  // Locked view: read-only rendering with no buttons or textareas.
  if (locked) {
    return (
      <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
        <CardBody>
          <Stack spacing="4">
            <Box>
              <Text fontWeight="semibold" color="smText" mb="1">
                {t("Qualification decision")}
              </Text>
              <Text fontSize="sm" color="smMuted">
                {t("The invitation is confirmed — the decision is final.")}
              </Text>
            </Box>

            {espdDigest && <EspdDigestText digest={espdDigest} />}

            {declaredGrounds.length > 0 && <DeclaredExclusionGrounds grounds={declaredGrounds} />}

            {recordedAssessment && <ExclusionGroundsAssessmentRecord assessment={recordedAssessment} />}

            {request.internalNote && (
              <Box>
                <Text fontSize="sm" fontWeight="medium" color="smText" mb="1">
                  {t("Internal note")}
                </Text>
                <Text fontSize="sm" color="smText">
                  {request.internalNote}
                </Text>
              </Box>
            )}

            {verdict && (
              <Box>
                <HStack mb="1">
                  <Badge colorScheme={verdict.qualified ? "green" : "red"}>
                    {verdict.qualified ? t("Qualified") : t("Not qualified")}
                  </Badge>
                </HStack>
                {isRejected && (
                  <RejectionDetails
                    grounds={request.rejection?.grounds}
                    reason={request.rejectionReason}
                    message={request.rejection?.message}
                    attachments={request.rejection?.attachments}
                    attachmentIds={request.rejection?.attachmentIds}
                  />
                )}
                <Text fontSize="sm" color="smMuted" mt="2">
                  {formatDecidedBy(verdict, displayDateWithTime, t)}
                </Text>
              </Box>
            )}
            <WithdrawnRejections withdrawn={request.withdrawnRejections} />
          </Stack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
      <CardBody>
        <Stack spacing="5">
          <Text fontWeight="semibold" color="smText">
            {t("Qualification decision")}
          </Text>

          {/* ESPD digest — informational only, never gates the verdict */}
          {espdDigest &&
            (espdDigest.flagged > 0 ? (
              <Alert status="info" rounded="md" fontSize="sm" py="2">
                <AlertIcon />
                <Button
                  variant="link"
                  fontSize="sm"
                  color="inherit"
                  onClick={onShowEspd}
                  fontWeight="normal"
                >
                  {t("ESPD: {{flagged}} of {{total}} declarations need attention — see the Exclusion grounds tab", {
                    flagged: espdDigest.flagged,
                    total: espdDigest.total,
                  })}
                </Button>
              </Alert>
            ) : (
              <EspdDigestText digest={espdDigest} />
            ))}

          {declaredGrounds.length > 0 && (
            <DeclaredExclusionGrounds grounds={declaredGrounds} onShowEspd={onShowEspd} />
          )}

          {recordedAssessment ? (
            <ExclusionGroundsAssessmentRecord assessment={recordedAssessment} />
          ) : (
            assessmentRequired && (
              <Box>
                <HStack mb="1" align="center">
                  <Text fontSize="sm" fontWeight="medium" color="smText">
                    {t("Assessment of exclusion grounds")}
                  </Text>
                  <SaveStatusBadge status={editor.statusFor("exclusionGroundsAssessment")} />
                </HStack>
                <Textarea
                  aria-label={t("Assessment of exclusion grounds") ?? undefined}
                  value={editor.values.exclusionGroundsAssessment}
                  onChange={(e) => editor.setField("exclusionGroundsAssessment", e.target.value)}
                  isReadOnly={!mayManage}
                  bg="smBackgroundSecondary"
                  rounded="md"
                  fontSize="sm"
                  mb="1"
                />
                <Text fontSize="xs" color="smMuted">
                  {t(
                    "Explain why the supplier can be qualified despite the declared grounds. Kept with the decision, never shared with the supplier.",
                  )}
                </Text>
              </Box>
            )
          )}

          {/* Internal note */}
          <Box>
            <HStack mb="1" align="center">
              <Text fontSize="sm" fontWeight="medium" color="smText">
                {t("Internal note")}
              </Text>
              <SaveStatusBadge status={editor.statusFor("internalNote")} />
            </HStack>
            <Textarea
              aria-label={t("Internal note") ?? undefined}
              value={editor.values.internalNote}
              onChange={(e) => editor.setField("internalNote", e.target.value)}
              isReadOnly={!mayManage}
              bg="smBackgroundSecondary"
              rounded="md"
              fontSize="sm"
              mb="1"
            />
            <Text fontSize="xs" color="smMuted">
              {t("Only visible to the buying team. Never shared with the supplier.")}
            </Text>
          </Box>

          {/* Verdict toggle */}
          {membershipEnded ? (
            <MembershipEnded
              request={request}
              givesNotice={!!detail.admissionRemovalGivesNotice}
              decidedBy={verdict ? formatDecidedBy(verdict, displayDateWithTime, t) : null}
            />
          ) : (
            <Box>
              {mayManage && (
                <QualificationVerdictControl
                  value={verdict?.qualified ?? null}
                  onChange={handleVerdictChange}
                  isReadOnly={isSettingVerdict}
                  isPositiveDisabled={awaitsQualification && (!allReviewed || assessmentMissing)}
                  isNegativeDisabled={endsByRemoval}
                  isClearable={clearable}
                  positiveLabel={t("Qualified")}
                  negativeLabel={t("Not qualified")}
                />
              )}

              {mayManage && !allReviewed && verdict?.qualified !== true && (
                <Text fontSize="xs" color="smMuted" mt="1">
                  {t("Review all qualification requirements first ({{count}} remaining)", {
                    count: pendingCount,
                  })}
                </Text>
              )}

              {mayManage && assessmentMissing && awaitsQualification && (
                <Text fontSize="xs" color="smMuted" mt="1">
                  {t("Write the assessment of the exclusion grounds first")}
                </Text>
              )}

              {verdict && (
                <Box mt="2" fontSize="sm" color="smMuted">
                  {!mayManage && (
                    <Text fontWeight="medium" color="smText">
                      {verdict.qualified ? t("Qualified") : t("Not qualified")}
                    </Text>
                  )}
                  <Text>
                    {formatDecidedBy(verdict, displayDateWithTime, t)}
                  </Text>
                  {mayManage && (
                    <Text fontSize="xs">
                      {endsByRemoval
                        ? detail.admissionRemovalGivesNotice
                          ? t(
                              "The supplier is admitted. To end its qualification, use «Remove from the qualification system» under «Suppliers», which gives the supplier written notice first.",
                            )
                          : t(
                              "The supplier is admitted. To end its membership, use «Remove from the system» under «Suppliers», which tells the supplier your reason.",
                            )
                        : clearable
                          ? t("Click the active choice to undo the decision")
                          : t("The decision has been sent to the supplier. To change it, choose the other decision.")}
                    </Text>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Registered rejection — read-only; undo it via the verdict toggle above */}
          {isRejected && (
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="smText" mb="2">
                {t("Avvisning")}
              </Text>
              <RejectionDetails
                grounds={request.rejection?.grounds}
                reason={request.rejectionReason}
                message={request.rejection?.message}
                attachments={request.rejection?.attachments}
                attachmentIds={request.rejection?.attachmentIds}
              />
              <Text fontSize="xs" color="smMuted" mt="2">
                {clearable
                  ? t("Sent to the supplier with the rejection when the invitation is confirmed.")
                  : t("Sent to the supplier with the rejection when it was decided.")}
              </Text>
            </Box>
          )}

          <WithdrawnRejections withdrawn={request.withdrawnRejections} />

          {/* Retracted-review warning — only for qualified verdicts; for rejections we show
              the "(N remaining)" helper above which is sufficient */}
          {verdict?.qualified === true && progress.hasRequirements && pendingCount > 0 && !membershipEnded && (
            <Alert status="warning" rounded="md" fontSize="sm" py="2">
              <AlertIcon />
              {t("The review changed after the decision — confirm it again.")}
            </Alert>
          )}
        </Stack>
      </CardBody>

      {withdrawalModal.isOpen && (
        <ReasonModal
          title={t("Withdraw the rejection")}
          description={t(
            "{{supplier}} was told it is not qualified. Say why the rejection is withdrawn. The rejection stays on the application and in the protocol, marked as withdrawn, with your reason. The supplier is told it is qualified, without the reason.",
            { supplier: request.organizationName ?? "" },
          )}
          placeholder={t("Reason for withdrawing the rejection") ?? undefined}
          confirmLabel={t("Withdraw rejection and qualify")}
          confirmColorScheme="teal"
          reason={withdrawalReason}
          onReasonChange={setWithdrawalReason}
          maxLength={MAX_WITHDRAWAL_REASON_LENGTH}
          isLoading={isSettingVerdict}
          isConfirmDisabled={alreadyWithdrawn}
          onConfirm={() => void submitWithdrawal()}
          onClose={withdrawalModal.onClose}
        >
          {alreadyWithdrawn && (
            <Alert status="info" rounded="md" fontSize="sm" mt="3">
              <AlertIcon />
              {t(REJECTION_ALREADY_WITHDRAWN)}
            </Alert>
          )}
        </ReasonModal>
      )}

      {rejectionModal.isOpen && (
        <RejectSupplierRequestModal
          eventId={eventId}
          requestId={request.id}
          supplierName={request.organizationName ?? ""}
          organizationNumber={request.organizationNumber}
          foaPart={detail.foaPart}
          isOpen={rejectionModal.isOpen}
          onClose={rejectionModal.onClose}
          onSubmit={submitRejection}
          isSubmitting={isSettingVerdict}
        />
      )}
    </Card>
  );
};

const EspdDigestText = ({ digest }: { digest: { flagged: number; total: number } }) => {
  const { t } = useTranslation();

  return (
    <Text fontSize="sm" color="smMuted">
      {digest.flagged > 0
        ? t("ESPD: {{flagged}} of {{total}} declarations need attention — see the Exclusion grounds tab", {
            flagged: digest.flagged,
            total: digest.total,
          })
        : t("ESPD: no declarations need attention")}
    </Text>
  );
};

const RejectSupplierRequestModal = ({
  eventId,
  requestId,
  supplierName,
  organizationNumber,
  foaPart,
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  eventId: string;
  requestId: string;
  supplierName: string;
  organizationNumber?: string | null;
  foaPart: BuyerParticipationRequestDetailDto["foaPart"];
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (submission: RejectionSubmission) => Promise<void>;
  isSubmitting: boolean;
}) => {
  const { data, refetch } = useGetParticipationRequestRejectionDocumentsQuery({
    eventId,
    requestId,
  });
  const { data: event } = useGetSourcingEventQuery({ eventId });
  const regime = useMemo(() => rejectionRegimeOf(event), [event]);
  const qualificationGroundKey = getQualificationRequirementGroundKey(foaPart, regime);

  return (
    <RejectSupplierModal
      isOpen={isOpen}
      onClose={onClose}
      supplierName={supplierName}
      organizationNumber={organizationNumber}
      foaPart={foaPart}
      regime={regime}
      initialGroundKeys={qualificationGroundKey ? [qualificationGroundKey] : []}
      outsideProcurementAct={event?.outsideProcurementAct ?? false}
      venue={rejectionVenueOf(event?.eventContext.type === "Public" ? event.eventContext.procedureType : null)}
      attachments={{
        entityType: "ParticipationRequestRejection",
        entityId: requestId,
        eventId,
        documents: data?.documents ?? [],
        refetch,
      }}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    />
  );
};

const MembershipEnded = ({
  request,
  givesNotice,
  decidedBy,
}: {
  request: BuyerParticipationRequestDetailDto["request"];
  givesNotice: boolean;
  decidedBy: string | null;
}) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const endedAt = displayDateWithTime(request.membershipEndedAt ?? "");
  const withdrew = request.status === ParticipationRequestStatus.Withdrawn;
  const name = request.membershipEndedByName;
  const badge = participationStatusBadge(request.status);
  const stillOnList =
    request.membershipEndsAt && Date.parse(request.membershipEndsAt) > Date.now() ? request.membershipEndsAt : null;
  const endedLine = membershipEndedLine(t, { withdrew, givesNotice, date: endedAt, name });

  return (
    <Box fontSize="sm">
      <HStack mb="1">
        <Badge colorScheme={badge.colorScheme}>{t(badge.labelKey)}</Badge>
      </HStack>
      <Text color="smText">{endedLine}</Text>
      {stillOnList && (
        <Text color="smChipAmberFg" mt="1">
          {t("On the list up to and including {{date}}", {
            date: displayDate(lastDayOnListBefore(stillOnList).toISOString()),
          })}
        </Text>
      )}
      {!withdrew && request.membershipEndReason && (
        <Box mt="2">
          <Text fontWeight="medium" color="smText">
            {t("Reason")}
          </Text>
          <Text color="smText" whiteSpace="pre-wrap">
            {request.membershipEndReason}
          </Text>
        </Box>
      )}
      {decidedBy && (
        <Text color="smMuted" mt="2">
          {t("Admitted earlier: {{decidedBy}}", { decidedBy })}
        </Text>
      )}
    </Box>
  );
};

const membershipEndedLine = (
  t: TFunction,
  { withdrew, givesNotice, date, name }: { withdrew: boolean; givesNotice: boolean; date: string; name?: string | null },
): string => {
  if (withdrew) {
    return givesNotice
      ? t("Withdrew from the qualification system {{date}}", { date })
      : t("Withdrew from the system {{date}}", { date });
  }
  if (givesNotice) {
    return name
      ? t("Removed from the qualification system {{date}} by {{name}}", { date, name })
      : t("Removed from the qualification system {{date}}", { date });
  }
  return name
    ? t("Removed from the system {{date}} by {{name}}", { date, name })
    : t("Removed from the system {{date}}", { date });
};

const WithdrawnRejections = ({ withdrawn }: { withdrawn?: WithdrawnRejectionDto[] }) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  if (!withdrawn?.length) {
    return null;
  }
  return (
    <Stack spacing="3">
      {withdrawn.map((past) => (
        <Box key={past.withdrawnAt} borderWidth="1px" borderColor="smBorder" rounded="md" p="3">
          <HStack mb="2" spacing="2">
            <Badge colorScheme="gray">{t("Withdrawn {{date}}", { date: displayDateWithTime(past.withdrawnAt) })}</Badge>
            <Text fontSize="sm" fontWeight="medium" color="smText">
              {t("Earlier rejection")}
            </Text>
          </HStack>
          <RejectionDetails
            grounds={past.rejection?.grounds}
            reason={past.rejectionReason}
            message={past.rejection?.message}
            attachments={past.rejection?.attachments}
            attachmentIds={past.rejection?.attachmentIds}
          />
          <Text fontSize="sm" color="smMuted" mt="2">
            {t("Rejected {{date}} by {{name}}", {
              date: displayDateWithTime(past.rejectedAt),
              name: past.rejectedByName ?? "–",
            })}
          </Text>
          <Text fontSize="sm" color="smMuted">
            {t("Withdrawn by {{name}}: {{reason}}", { name: past.withdrawnByName ?? "–", reason: past.reason })}
          </Text>
        </Box>
      ))}
    </Stack>
  );
};

const REJECTION_ALREADY_WITHDRAWN = "zz_SourcingEventApiErrorCode_RejectionAlreadyWithdrawn";

const errorCodeOf = (error: unknown): string | undefined =>
  (error as { data?: { errorCode?: string } } | null)?.data?.errorCode;

const MAX_WITHDRAWAL_REASON_LENGTH = 2000;
